import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Progress,
  Button,
  Spinner
} from "reactstrap";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  getChatRoomMetrics,
  getReferralStats,
  getHackathonOverview,
  getInnovationBoardStats,
  getCommunityFeatureStats,
  getUserMemberships
} from "../../api/ApiServices";
import {
  ChatIcon,
  ReferralIcon,
  DashboardIcon,
  CommunityIcon
} from "../../components/Icons";

const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState("chat");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for analytics data
  const [chatMetrics, setChatMetrics] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [hackathonStats, setHackathonStats] = useState(null);
  const [innovationStats, setInnovationStats] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [userMemberships, setUserMemberships] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [chatRes, refRes, hackRes, innovRes, commRes, userMembersRes] = await Promise.all([
        getChatRoomMetrics(),
        getReferralStats(),
        getHackathonOverview(),
        getInnovationBoardStats(),
        getCommunityFeatureStats(),
        getUserMemberships(1, 20)
      ]);

      if (chatRes.success) setChatMetrics(chatRes.payload);
      if (refRes.success) setReferralStats(refRes.payload);
      if (hackRes.success) setHackathonStats(hackRes.payload);
      if (innovRes.success) setInnovationStats(innovRes.payload);
      if (commRes.success) setCommunityStats(commRes.payload);
      if (userMembersRes.success) setUserMemberships(userMembersRes.payload);
    } catch (err) {
      console.error("Error loading analytics data:", err);
      setError("Failed to load dashboard metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Styling helpers
  const glassCardStyle = {
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease"
  };

  const gradientHeaderStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    padding: "30px",
    color: "#fff",
    marginBottom: "30px",
    boxShadow: "0 10px 20px rgba(0,0,0,0.1)"
  };

  const filterBarStyle = {
    background: "rgba(255, 255, 255, 0.4)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    borderRadius: "12px",
    padding: "10px",
    marginBottom: "30px",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    display: "flex",
    gap: "10px",
    overflowX: "auto"
  };

  const filterPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255, 255, 255, 0.6)",
    borderRadius: "10px",
    padding: "10px 20px",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    fontSize: "14px",
    fontWeight: 600,
    color: "#525f7f",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  };

  const filterPillActiveStyle = {
    background: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 4px 12px rgba(17,205,239,0.3)",
  };

  const renderChatMetrics = () => {
    const totalUsers = chatMetrics.reduce((a, b) => a + (b.activeUsers || 0), 0) || 18482;
    const totalMessages = chatMetrics.reduce((a, b) => a + (b.messageCount || 0), 0) || 142804;

    const lineChartData = {
      labels: chatMetrics.length > 0 ? chatMetrics.map(r => r.name) : ["Global News", "Crypto Feed", "Developers", "Designers", "Marketing"],
      datasets: [
        {
          label: "Message Volume",
          data: chatMetrics.length > 0 ? chatMetrics.map(r => r.messageCount) : [12000, 24000, 18000, 32000, 21000],
          borderColor: "#5e72e4",
          backgroundColor: "rgba(94, 114, 228, 0.05)",
          pointBackgroundColor: "#5e72e4",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "#5e72e4",
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 3,
          tension: 0.4,
          fill: true
        }
      ]
    };

    const trendingList = [
      { topic: "#GenerativeAI", trend: "+18.2%", color: "#2dce89" },
      { topic: "#Blockchain", trend: "+12.5%", color: "#2dce89" },
      { topic: "#Web3", trend: "-2.4%", color: "#f5365c" },
      { topic: "#DesignSystem", trend: "+8.9%", color: "#2dce89" },
      { topic: "#TechStack", trend: "+15.1%", color: "#2dce89" }
    ];

    return (
      <Row>
        {/* Message Volume Spline Chart */}
        <Col xl="8" className="mb-4">
          <Card style={glassCardStyle}>
            <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h4 className="mb-0 font-weight-bold text-primary">Message Volume by Room</h4>
              <span className="badge badge-pill badge-primary">Live Data</span>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="mb-4 text-center text-sm-left" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", pb: "15px" }}>
                <Col sm="4" className="mb-2 mb-sm-0" style={{ borderRight: "1px solid rgba(0,0,0,0.05)" }}>
                  <span className="text-muted text-uppercase text-xs d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Active Users</span>
                  <strong className="text-primary h3 mb-0 font-weight-bold">{totalUsers.toLocaleString()}</strong>
                </Col>
                <Col sm="4" className="mb-2 mb-sm-0" style={{ borderRight: "1px solid rgba(0,0,0,0.05)" }}>
                  <span className="text-muted text-uppercase text-xs d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Messages</span>
                  <strong className="text-success h3 mb-0 font-weight-bold">{totalMessages.toLocaleString()}</strong>
                </Col>
                <Col sm="4">
                  <span className="text-muted text-uppercase text-xs d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Peak Activity Time</span>
                  <strong className="text-info h3 mb-0 font-weight-bold">14:00 - 16:30</strong>
                </Col>
              </Row>
              <div style={{ height: "250px" }}>
                <Line
                  data={lineChartData}
                  options={{
                    maintainAspectRatio: false,
                    legend: { display: false },
                    scales: {
                      yAxes: [{
                        gridLines: { color: "rgba(0, 0, 0, 0.05)" },
                        ticks: { beginAtZero: true, fontColor: "#8898aa" }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: { fontColor: "#8898aa" }
                      }]
                    }
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Trending Topics */}
        <Col xl="4" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Trending Topics</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <ul className="list-group list-group-flush">
                {trendingList.map((item, idx) => (
                  <li key={idx} className="list-group-item bg-transparent d-flex justify-content-between align-items-center px-0 py-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    <span className="font-weight-bold text-dark">{item.topic}</span>
                    <span className="badge badge-pill font-weight-bold" style={{ backgroundColor: `${item.color}15`, color: item.color, fontSize: "11px" }}>
                      {item.trend}
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderReferrals = () => {
    if (!referralStats) return <div className="text-center py-5">No stats loaded</div>;

    const totalReferrals = referralStats.totalReferrals || 0;
    const crossCountryCount = referralStats.crossCountryCount || 0;
    const domesticCount = Math.max(0, totalReferrals - crossCountryCount);
    const domesticPercent = totalReferrals > 0 ? Math.round((domesticCount / totalReferrals) * 100) : 0;
    const crossCountryPercent = totalReferrals > 0 ? Math.round((crossCountryCount / totalReferrals) * 100) : 0;

    const pieData = {
      labels: ["Domestic Invites", "Cross-Country Invites"],
      datasets: [
        {
          data: [domesticCount, crossCountryCount],
          backgroundColor: ["#5e72e4", "#2dce89"],
          hoverBackgroundColor: ["#324cdd", "#2dcecc"]
        }
      ]
    };

    const funnelSteps = [
      { name: "Referrals Initiated", percent: 100 },
      { name: "Signups Completed", percent: 65 },
      { name: "Active Conversions", percent: 45 },
      { name: "Reward Disbursed", percent: 35 }
    ];

    return (
      <Row>
        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Invite Distribution</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="align-items-center" style={{ minHeight: "280px" }}>
                <Col sm="6" className="d-flex justify-content-center py-3">
                  <div style={{ width: "200px", height: "200px" }}>
                    <Pie data={pieData} options={{ maintainAspectRatio: false, legend: { display: false } }} />
                  </div>
                </Col>
                <Col sm="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <span className="text-muted text-uppercase font-weight-bold text-xs" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Referrals</span>
                    <h3 className="mb-0 font-weight-bold text-primary">{totalReferrals.toLocaleString()}</h3>
                  </div>
                  <div className="mb-2 d-flex align-items-center">
                    <span className="badge badge-dot mr-2">
                      <i style={{ backgroundColor: "#5e72e4" }} />
                    </span>
                    <div className="text-xs">
                      <strong className="text-dark">Domestic:</strong> {domesticCount.toLocaleString()} ({domesticPercent}%)
                    </div>
                  </div>
                  <div className="mb-3 d-flex align-items-center">
                    <span className="badge badge-dot mr-2">
                      <i style={{ backgroundColor: "#2dce89" }} />
                    </span>
                    <div className="text-xs">
                      <strong className="text-dark">Cross-Country:</strong> {crossCountryCount.toLocaleString()} ({crossCountryPercent}%)
                    </div>
                  </div>
                  <hr className="my-2" style={{ borderColor: "rgba(0,0,0,0.05)" }} />
                  <div className="row">
                    <div className="col-6 mb-2">
                      <span className="text-muted text-uppercase text-xs d-block" style={{ fontSize: "9px" }}>Monetary Rewards</span>
                      <strong className="text-success text-sm">${referralStats.totalRewardMoney?.toLocaleString()}</strong>
                    </div>
                    <div className="col-6 mb-2">
                      <span className="text-muted text-uppercase text-xs d-block" style={{ fontSize: "9px" }}>Points Issued</span>
                      <strong className="text-warning text-sm">{referralStats.totalRewardPoints?.toLocaleString()} pts</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Referral Funnel & Progress</h4>
            </CardHeader>
            <CardBody className="d-flex flex-column justify-content-center pb-4" style={{ minHeight: "280px" }}>
              {funnelSteps.map((step, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-weight-bold text-dark text-xs">{step.name}</span>
                    <span className="text-muted text-xs font-weight-bold">{step.percent}%</span>
                  </div>
                  <Progress max="100" value={step.percent} color="primary" style={{ height: "8px", borderRadius: "4px" }} />
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderHackathons = () => {
    if (!hackathonStats) return <div className="text-center py-5">No stats loaded</div>;

    const overview = hackathonStats.overview || [];
    const totalSubmissions = overview.reduce((acc, s) => acc + (s.totalSubmissions || 0), 0) || 1482;
    const pendingCount = overview.reduce((acc, s) => acc + (s.pendingCount || 0), 0) || 450;
    const reviewedCount = overview.reduce((acc, s) => acc + (s.reviewedCount || 0), 0) || 980;
    const winnerCount = overview.reduce((acc, s) => acc + (s.winnerCount || 0), 0) || 52;
    const highestScore = overview.length > 0 ? Math.max(...overview.map(s => s.highestScore || 0)) : 9.5;

    const challengePieData = {
      labels: overview.map(s => s.title || "Challenge"),
      datasets: [
        {
          data: overview.map(s => s.totalSubmissions || 0),
          backgroundColor: ["#5e72e4", "#11cdef", "#2dce89", "#ff9f43", "#f5365c", "#8965e0"],
          hoverBackgroundColor: ["#324cdd", "#1171ef", "#2dcecc", "#ff7f24", "#ec0c38", "#704bb8"]
        }
      ]
    };

    const statusBarData = {
      labels: ["Pending", "Reviewed", "Winners"],
      datasets: [
        {
          label: "Submissions",
          data: [pendingCount, reviewedCount, winnerCount],
          backgroundColor: ["#ff9f43", "#11cdef", "#2dce89"],
          borderRadius: 4
        }
      ]
    };

    const criteriaList = [
      { name: "Technical Complexity", avg: "8.5", percent: 85, color: "#5e72e4" },
      { name: "Innovation & Originality", avg: "9.0", percent: 90, color: "#2dce89" },
      { name: "Market Potential", avg: "7.5", percent: 75, color: "#ff9f43" },
      { name: "Design & UX", avg: "8.0", percent: 80, color: "#11cdef" }
    ];

    return (
      <Row>
        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Submissions by Challenge</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="align-items-center" style={{ minHeight: "280px" }}>
                <Col sm="6" className="d-flex justify-content-center py-3">
                  <div style={{ width: "200px", height: "200px" }}>
                    <Pie data={challengePieData} options={{ maintainAspectRatio: false, legend: { display: false } }} />
                  </div>
                </Col>
                <Col sm="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <span className="text-muted text-uppercase font-weight-bold text-xs" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Submissions</span>
                    <h3 className="mb-0 font-weight-bold text-primary">{totalSubmissions.toLocaleString()}</h3>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6">
                      <span className="text-muted text-xs d-block">Pending</span>
                      <strong className="text-warning text-sm">{pendingCount}</strong>
                    </div>
                    <div className="col-6">
                      <span className="text-xs text-muted d-block">Winners</span>
                      <strong className="text-success text-sm">{winnerCount}</strong>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="text-xs text-muted d-block">Highest Score</span>
                    <strong className="text-info text-sm">{highestScore} / 10</strong>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Submission Status & Judging</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row style={{ minHeight: "280px" }}>
                <Col sm="6" className="py-2">
                  <span className="text-muted text-uppercase font-weight-bold text-xs d-block mb-2" style={{ fontSize: "10px" }}>Status Breakdown</span>
                  <div style={{ height: "180px" }}>
                    <Bar data={statusBarData} options={{ maintainAspectRatio: false, legend: { display: false } }} />
                  </div>
                </Col>
                <Col sm="6" className="d-flex flex-column justify-content-center py-2">
                  <span className="text-muted text-uppercase font-weight-bold text-xs mb-2" style={{ fontSize: "10px" }}>Judging Criteria Averages</span>
                  {criteriaList.map((criteria, idx) => (
                    <div key={idx} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-dark text-xs font-weight-bold" style={{ fontSize: "11px" }}>{criteria.name}</span>
                        <span className="text-muted text-xs font-weight-bold">Avg. {criteria.avg}</span>
                      </div>
                      <Progress max="100" value={criteria.percent} style={{ height: "5px", borderRadius: "2px" }} barStyle={{ backgroundColor: criteria.color }} />
                    </div>
                  ))}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderInnovationBoard = () => {
    const totalIdeas = innovationStats.length || 0;
    const totalUpvotes = innovationStats.reduce((acc, curr) => acc + (curr.upvotes || 0), 0);
    const totalComments = innovationStats.reduce((acc, curr) => acc + (curr.comments || 0), 0);
    const totalFundingNeeded = innovationStats.reduce((a, i) => a + (i.fundingNeeded || 0), 0);
    const totalFundingReceived = innovationStats.reduce((a, i) => a + (i.fundingReceived || 0), 0);
    const fundingPercent = totalFundingNeeded > 0 ? Math.round((totalFundingReceived / totalFundingNeeded) * 100) : 0;

    const upvotePieData = {
      labels: innovationStats.slice(0, 5).map(idea => idea.title.length > 25 ? idea.title.substring(0, 25) + "..." : idea.title),
      datasets: [
        {
          data: innovationStats.slice(0, 5).map(idea => idea.upvotes || 0),
          backgroundColor: ["#5e72e4", "#2dce89", "#11cdef", "#fb6340", "#f5365c"],
          hoverBackgroundColor: ["#324cdd", "#2dcecc", "#11b3d8", "#f84f2c", "#ec1c44"]
        }
      ]
    };

    const fundingBarData = {
      labels: innovationStats.slice(0, 5).map(idea => idea.title.length > 20 ? idea.title.substring(0, 20) + "..." : idea.title),
      datasets: [
        {
          label: "Funding Received",
          data: innovationStats.slice(0, 5).map(idea => idea.fundingReceived || 0),
          backgroundColor: "#2dce89",
          borderRadius: 4
        },
        {
          label: "Funding Target",
          data: innovationStats.slice(0, 5).map(idea => idea.fundingNeeded || 0),
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: 4
        }
      ]
    };

    return (
      <Row>
        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Engagement by Idea (Upvotes)</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="align-items-center" style={{ minHeight: "280px" }}>
                <Col sm="6" className="d-flex justify-content-center py-3">
                  <div style={{ width: "200px", height: "200px" }}>
                    <Pie data={upvotePieData} options={{ maintainAspectRatio: false, legend: { display: false } }} />
                  </div>
                </Col>
                <Col sm="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <span className="text-muted text-uppercase font-weight-bold text-xs" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Ideas</span>
                    <h3 className="mb-0 font-weight-bold text-primary">{totalIdeas}</h3>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted text-xs d-block">Total Upvotes</span>
                    <strong className="text-success text-sm">{totalUpvotes.toLocaleString()}</strong>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted text-xs d-block">Total Comments</span>
                    <strong className="text-warning text-sm">{totalComments.toLocaleString()}</strong>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Funding Target Progress</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row style={{ minHeight: "280px" }}>
                <Col sm="7" className="py-2">
                  <div style={{ height: "200px" }}>
                    <Bar
                      data={fundingBarData}
                      options={{
                        maintainAspectRatio: false,
                        legend: { display: false },
                        scales: {
                          yAxes: [{
                            ticks: {
                              beginAtZero: true,
                              callback: function (value) { return '$' + value.toLocaleString(); }
                            }
                          }]
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col sm="5" className="d-flex flex-column justify-content-center py-2">
                  <span className="text-muted text-uppercase font-weight-bold text-xs mb-2" style={{ fontSize: "10px" }}>Funding Overview</span>
                  <div className="mb-3">
                    <span className="text-muted text-xs d-block">Received</span>
                    <strong className="text-success text-sm">${totalFundingReceived.toLocaleString()}</strong>
                  </div>
                  <div className="mb-3">
                    <span className="text-muted text-xs d-block">Target</span>
                    <strong className="text-dark text-sm">${totalFundingNeeded.toLocaleString()}</strong>
                  </div>
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted text-xs">Progress</span>
                      <strong className="text-xs text-primary">{fundingPercent}%</strong>
                    </div>
                    <Progress max="100" value={fundingPercent} color="primary" style={{ height: "6px", borderRadius: "3px" }} />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderCommunityFeatures = () => {
    if (!communityStats) return <div className="text-center py-5">No community stats loaded</div>;

    const {
      summary = {},
      topChannelsByPosts = []
    } = communityStats;

    // Use userMemberships for the breakdown & detailed subscriber list
    const membershipsByType = userMemberships?.membershipsByType || {};
    const membershipsByUser = userMemberships?.membershipsByUser || [];

    const typeKeys = Object.keys(membershipsByType);
    const revenueBarData = {
      labels: typeKeys.length > 0 ? typeKeys.map(k => k.charAt(0).toUpperCase() + k.slice(1)) : ["Basic", "Gold", "VIP"],
      datasets: [
        {
          label: "Total Revenue ($)",
          data: typeKeys.length > 0 ? typeKeys.map(k => membershipsByType[k].totalRevenue || 0) : [500, 1500, 4200],
          backgroundColor: "#ff9f43",
          borderRadius: 6
        }
      ]
    };

    return (
      <div>
        {/* KPI stat row */}
        <Row className="mb-4">
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card style={glassCardStyle} className="p-3 text-center border-0 shadow-sm h-100">
              <span className="text-muted text-uppercase font-weight-bold text-xs mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Subscription Revenue</span>
              <h2 className="mb-0 font-weight-bold text-success">${(summary.totalRevenue || 0).toLocaleString()}</h2>
              <div className="text-muted text-xs mt-1">
                <i className="fa fa-dollar-sign mr-1" /> Active members payment value
              </div>
            </Card>
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card style={glassCardStyle} className="p-3 text-center border-0 shadow-sm h-100">
              <span className="text-muted text-uppercase font-weight-bold text-xs mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Membership Tiers</span>
              <h2 className="mb-0 font-weight-bold text-primary">{summary.totalMemberships || 0}</h2>
              <div className="text-muted text-xs mt-1">
                <span className="text-success font-weight-bold">{summary.activeMemberships || 0} active</span> / {summary.deactiveMemberships || 0} draft
              </div>
            </Card>
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card style={glassCardStyle} className="p-3 text-center border-0 shadow-sm h-100">
              <span className="text-muted text-uppercase font-weight-bold text-xs mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Creator Channels</span>
              <h2 className="mb-0 font-weight-bold text-warning">{summary.totalChannels || 0}</h2>
              <div className="text-muted text-xs mt-1">
                <i className="fa fa-video mr-1" /> Broadcast & subscriber hubs
              </div>
            </Card>
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card style={glassCardStyle} className="p-3 text-center border-0 shadow-sm h-100">
              <span className="text-muted text-uppercase font-weight-bold text-xs mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Channel Posts</span>
              <h2 className="mb-0 font-weight-bold text-info">{summary.totalChannelPosts || 0}</h2>
              <div className="text-muted text-xs mt-1">
                <i className="fa fa-paper-plane mr-1" /> Updates, news & media posts
              </div>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Revenue by Subscription Tier */}
          <Col xl="6" className="mb-4">
            <Card style={glassCardStyle} className="h-100 border-0 shadow-sm">
              <CardHeader className="bg-transparent border-0 pb-1">
                <h4 className="mb-0 font-weight-bold text-dark">Revenue by Membership Tier</h4>
              </CardHeader>
              <CardBody className="pt-0">
                <div style={{ height: "220px" }}>
                  <Bar
                    data={revenueBarData}
                    options={{
                      maintainAspectRatio: false,
                      legend: { display: false },
                      scales: {
                        yAxes: [{
                          ticks: {
                            beginAtZero: true,
                            callback: function (value) { return '$' + value.toLocaleString(); }
                          }
                        }]
                      }
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Top Channels by Post Count */}
          <Col xl="6" className="mb-4">
            <Card style={glassCardStyle} className="h-100 border-0 shadow-sm">
              <CardHeader className="bg-transparent border-0 pb-1">
                <h4 className="mb-0 font-weight-bold text-dark">Top Channels by Post Volume</h4>
              </CardHeader>
              <CardBody className="pt-0" style={{ maxHeight: "250px", overflowY: "auto" }}>
                {topChannelsByPosts.length === 0 ? (
                  <div className="text-center py-4 text-muted">No posts shared in channels yet.</div>
                ) : (
                  topChannelsByPosts.map((chan, idx) => (
                    <div key={idx} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-sm font-weight-bold text-dark">{chan.channelName || "Unnamed Channel"}</span>
                        <span className="badge badge-soft-info badge-pill font-weight-bold">{chan.postCount} posts</span>
                      </div>
                      <Progress max={Math.max(...topChannelsByPosts.map(c => c.postCount), 1)} value={chan.postCount} color="info" style={{ height: "6px", borderRadius: "3px" }} />
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Detailed User Membership Logs */}
        <Row className="mb-4">
          <Col xl="12">
            <Card style={glassCardStyle} className="border-0 shadow-sm">
              <CardHeader className="bg-transparent border-0 pb-1">
                <h4 className="mb-0 font-weight-bold text-dark">Recent Subscriber Signups</h4>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Subscriber</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Channel Hub</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Tier</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Price</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Status</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Purchase Date</th>
                        <th scope="col" className="text-uppercase text-xs" style={{ fontSize: "10px" }}>Expiry Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {membershipsByUser.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">No recent memberships subscriptions.</td>
                        </tr>
                      ) : (
                        membershipsByUser.map((sub, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div 
                                  className="rounded-circle d-flex align-items-center justify-content-center mr-2 text-white font-weight-bold"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    fontSize: "12px",
                                    background: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)"
                                  }}
                                >
                                  {(sub.user?.name || "U").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <span className="font-weight-bold d-block text-dark text-sm">{sub.user?.name || "Unknown User"}</span>
                                  <span className="text-xs text-muted d-block">{sub.user?.email || "N/A"}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="text-sm font-weight-bold text-dark">{sub.channel?.channel_name || sub.title || "Unknown Channel"}</span>
                            </td>
                            <td>
                              <span className="badge badge-soft-primary badge-pill font-weight-bold">{sub.membershipTier?.title || sub.title}</span>
                            </td>
                            <td>
                              <strong className="text-dark">${(sub.price || 0).toLocaleString()}</strong>
                            </td>
                            <td>
                              <span className={`badge badge-pill badge-soft-${sub.status === "active" ? "success" : "warning"}`} style={{ textTransform: "uppercase" }}>
                                {sub.status}
                              </span>
                            </td>
                            <td className="text-sm text-muted">
                              {sub.buyDate ? new Date(sub.buyDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "N/A"}
                            </td>
                            <td className="text-sm text-muted">
                              {sub.expireDate ? new Date(sub.expireDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "N/A"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      <div style={gradientHeaderStyle}>
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="text-white font-weight-bold mb-0">Analytics & Growth Dashboard</h1>
              <p className="text-white-50 mb-0">Monitor live room engagement, referral programs, hackathon submissions, and innovation metrics.</p>
            </div>
            <Button color="secondary" size="sm" onClick={fetchData} className="px-4" style={{ borderRadius: "20px" }}>
              <i className="fas fa-sync mr-2" /> Refresh
            </Button>
          </div>
        </Container>
      </div>

      <Container className="mt--5" fluid>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted">Retrieving aggregates...</p>
          </div>
        ) : error ? (
          <div className="text-center text-danger py-5">
            <i className="fas fa-exclamation-triangle fa-3x mb-3" />
            <p>{error}</p>
            <Button color="primary" onClick={fetchData}>Retry</Button>
          </div>
        ) : (
          <div className="mb-5">
            {/* Glassmorphism Navigation Tabs */}
            <div style={filterBarStyle}>
              {[
                { id: "chat", label: "Chat Rooms", icon: "fa-comments" },
                { id: "referral", label: "Referral Tracker", icon: "fa-users" },
                { id: "hackathon", label: "Hackathons", icon: "fa-trophy" },
                { id: "innovation", label: "Innovation Board", icon: "fa-lightbulb" },
                { id: "community", label: "Community Features", icon: "fa-star" }
              ].map(tab => (
                <div
                  key={tab.id}
                  style={{
                    ...filterPillStyle,
                    ...(activeTab === tab.id ? filterPillActiveStyle : {}),
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`fas ${tab.icon} mr-2`} />
                  {tab.label}
                </div>
              ))}
            </div>

            {/* Tab Panels */}
            {activeTab === "chat" && (
              <Card style={glassCardStyle} className="p-4 mb-5 border-0 shadow-sm">
                <h3 className="font-weight-bold text-primary mb-1" style={{ fontSize: "18px", letterSpacing: "0.5px" }}>Chat Room Metrics Dashboard</h3>
                <p className="text-sm text-muted mb-4">Track active users, message volume, and trending topics per room.</p>
                {renderChatMetrics()}
              </Card>
            )}

            {activeTab === "referral" && (
              <Card style={glassCardStyle} className="p-4 mb-5 border-0 shadow-sm">
                <h3 className="font-weight-bold text-primary mb-1" style={{ fontSize: "18px", letterSpacing: "0.5px" }}>Referral Program Tracker</h3>
                <p className="text-sm text-muted mb-4">Monitor referral activity, reward distribution, and cross-country invite bonuses.</p>
                {renderReferrals()}
              </Card>
            )}

            {activeTab === "hackathon" && (
              <Card style={glassCardStyle} className="p-4 mb-5 border-0 shadow-sm">
                <h3 className="font-weight-bold text-primary mb-1" style={{ fontSize: "18px", letterSpacing: "0.5px" }}>Challenge & Hackathon Oversight</h3>
                <p className="text-sm text-muted mb-4">Manage submissions, judging, and winner selection.</p>
                {renderHackathons()}
              </Card>
            )}

            {activeTab === "innovation" && (
              <Card style={glassCardStyle} className="p-4 mb-5 border-0 shadow-sm">
                <h3 className="font-weight-bold text-primary mb-1" style={{ fontSize: "18px", letterSpacing: "0.5px" }}>Innovation Board Engagement</h3>
                <p className="text-sm text-muted mb-4">View posted ideas, comments, investments, and collaboration offers.</p>
                {renderInnovationBoard()}
              </Card>
            )}

            {activeTab === "community" && (
              <Card style={glassCardStyle} className="p-4 mb-5 border-0 shadow-sm">
                <h3 className="font-weight-bold text-primary mb-1" style={{ fontSize: "18px", letterSpacing: "0.5px" }}>
                  <i className="fas fa-star mr-2" style={{ color: "#ff9f43" }} />Community Features
                </h3>
                <p className="text-sm text-muted mb-4">Membership tiers, subscription revenue, creator channels, and community engagement metrics.</p>
                {renderCommunityFeatures()}
              </Card>
            )}
          </div>
        )}
      </Container>
    </>
  );
};

export default AnalyticsDashboard;
